'use client';

import { Activity, TransitionEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { UserActionLinks } from '@/features/layout/components/user-action-links';
import { ResponsiveNavMenu } from '@/features/layout/components/responsive-nav-menu';
import { SocialLoginLinks } from '@/shared/components/social-login-links';

type ResponsiveMenuProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ResponsiveMenu = ({ isOpen, onClose }: ResponsiveMenuProps) => {
  // The closed panel is only moved off-screen, where the router's viewport prefetch still counts
  // its links as visible (and they'd stay in the tab order). Hide the content with Activity once
  // the close transition ends: `display: none`, links detached, state kept for the next open.
  const [isContentVisible, setIsContentVisible] = useState(isOpen);
  if (isOpen && !isContentVisible) {
    setIsContentVisible(true);
  }

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (!isOpen && event.target === event.currentTarget && event.propertyName === 'transform') {
      setIsContentVisible(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`bg-background-panel fixed top-0 left-0 z-50 h-full w-[80%] max-w-96 min-w-80 transform px-4 py-6 transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
      >
        <Activity mode={isContentVisible ? 'visible' : 'hidden'}>
          <div className='flex h-full flex-col gap-8 pb-4 max-lg:mt-8'>
            <UserActionLinks onLinkClick={onClose} isMobileView />
            <ResponsiveNavMenu onLinkClick={onClose} />
            <SocialLoginLinks className='pl-2' />
          </div>
        </Activity>
        <button onClick={onClose} className='absolute top-6 right-6'>
          <X size={24} />
        </button>
      </div>
    </>
  );
};
